import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getIn } from '../../utils/ChangeSpy';
import {
  saveAddOnProject,
  changeField,
  revertUpdate,
  findUsers,
  findClients,
  findExecutive,
  getProjectsApprovalLists,
} from '../../actions/company/buyer/addOnProject/addOnProjectCreate';
import { getCompanyInfo } from '../../actions/companyDetail';
import AutoCompleteContainer from '../AutoComplete';
import { usePopupContext } from '../../contexts/PopupContext';

const AddAddonProjectPopupContainer = ({
  history,
  popup,
  user,
  isValid,
  addOnProjectCreate,
  approvalList,
  onErrorClose,
  revertUpdate,
  changeField,
  getProjectsApprovalLists,
  saveAddOnProject,
  getCompanyInfo,
  findUsers,
  findClients,
  findExecutive,
}) => {
  const { closePopup } = usePopupContext();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const companyId = popup.getIn(['props', 'companyId']);

    revertUpdate();
    getProjectsApprovalLists({ companyId });

    changeField({
      name: 'harvcoLead',
      value: user.get('module') || '',
    });
    changeField({
      name: 'harvcoLeadId',
      value: user.get('moduleId') || null,
    });
  }, []);

  const handleChange = event => {
    const { name, value: targetValue, checked, type } = event.target;
    const value = type === 'checkbox' ? checked : targetValue;

    return changeField({ name, value });
  };

  const handleChangeApprovalListIds = (name, value) => {
    changeField({ name, value });
  };

  const handleClose = () => {
    revertUpdate();
    closePopup();
  };

  const handleUserSuggestChange = ({ name, value }) => changeField({ name, value });

  const handleSave = () => {
    setLoading(true);

    const companyId = popup.getIn(['props', 'companyId']);
    const clientLeadId = getIn(addOnProjectCreate, 'clientLeadId');
    const execId = getIn(addOnProjectCreate, 'execId');
    const harvcoLeadId = getIn(addOnProjectCreate, 'harvcoLeadId');

    const body = {
      buyerId: companyId,
      category: getIn(addOnProjectCreate, 'category'),
      details: getIn(addOnProjectCreate, 'details'),
      status: getIn(addOnProjectCreate, 'status'),
      active: getIn(addOnProjectCreate, 'active'),
      type: 'Add-on',
      approvalListIds: getIn(addOnProjectCreate, 'approvalListIds'),
      isDefaultAddOn: false,
    };

    if (clientLeadId !== '') {
      body.clientLeadId = clientLeadId;
    }

    if (execId !== '') {
      body.execId = execId;
    }

    if (harvcoLeadId !== '') {
      body.harvcoLeadId = harvcoLeadId;
    }

    saveAddOnProject({
      body,
      afterSuccess: ({ response }) => {
        setLoading(false);
        handleClose();
        getCompanyInfo({ companyId });
        setTimeout(() => {
          history.push(`/company/${companyId}/buyer/add-on-project/${response.id}`);
        });
      },
      afterError: () => {
        setLoading(false);
      },
    });
  };

  const getUserSuggestions = () => (
    <AutoCompleteContainer
      idName="harvcoLeadId"
      inputProps={{
        name: 'harvcoLead',
        className: 'form-control',
        label: 'HarvCo Lead',
      }}
      name="harvcoLead"
      onChange={handleUserSuggestChange}
      onFind={options => {
        findUsers({
          ...options,
          field: 'harvcoLead',
        });
      }}
      rootPath={['targetCompany', 'buyer', 'addOnProject', 'addOnProjectCreate']}
      suggestionsName="suggestsUser"
      valueName="harvcoLead"
    />
  );

  const getClientSuggestions = () => {
    const companyId = popup.getIn(['props', 'companyId']);

    return (
      <AutoCompleteContainer
        idName="clientLeadId"
        inputProps={{
          name: 'clientLead',
          className: 'form-control',
          label: 'Client Lead',
        }}
        name="clientLead"
        onChange={handleUserSuggestChange}
        onFind={options => {
          findClients({
            ...options,
            data: { companyId },
            field: 'clientLead',
          });
        }}
        rootPath={['targetCompany', 'buyer', 'addOnProject', 'addOnProjectCreate']}
        suggestionsName="suggestsClient"
        valueName="clientLead"
      />
    );
  };

  const getExecutiveSuggestions = () => (
    <AutoCompleteContainer
      idName="execId"
      inputProps={{
        name: 'executive',
        className: 'form-control',
        label: 'Executive',
      }}
      name="executive"
      onChange={handleUserSuggestChange}
      onFind={options => {
        findExecutive({
          ...options,
          field: 'executive',
        });
      }}
      rootPath={['targetCompany', 'buyer', 'addOnProject', 'addOnProjectCreate']}
      suggestionsName="suggestsExecutive"
      valueName="executive"
    />
  );

  return (
    <Popups.AddAddonProjectPopup
      addOnProjectCreate={addOnProjectCreate}
      approvalList={approvalList}
      isValid={isValid}
      loading={loading}
      onChange={handleChange}
      onChangeApprovalListIds={handleChangeApprovalListIds}
      onClose={handleClose}
      onErrorClose={onErrorClose}
      onSave={handleSave}
      suggestClient={getClientSuggestions()}
      suggestExecutive={getExecutiveSuggestions()}
      suggestUser={getUserSuggestions()}
    />
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    inputErrors: state.targetCompany.buyer.addOnProject.addOnProjectCreate.get('inputErrors', Map()),
    isValid: state.targetCompany.buyer.addOnProject.addOnProjectCreate.get('isValid', false),
    addOnProjectCreate: state.targetCompany.buyer.addOnProject.addOnProjectCreate,
    approvalList: state.targetCompany.buyer.addOnProject.approvalList,
    user: state.auth.get('user', null),
  };
}

const AddAddonProjectPopupContainerMemo = memo(AddAddonProjectPopupContainer);

export { AddAddonProjectPopupContainerMemo as AddAddonProjectPopupContainer };

export default connect(
  mapStateToProps,
  {
    getCompanyInfo,
    getProjectsApprovalLists,
    saveAddOnProject,
    changeField,
    revertUpdate,
    findUsers,
    findClients,
    findExecutive,
  },
  mergeProps,
  connectOptions,
)(withRouter(AddAddonProjectPopupContainerMemo));
